/**
 * Bone-mapping modal: maps this app's humanoid slots to the actor's source
 * (FBX) bones. Standard rigs auto-match through the alias table; this UI is the
 * escape hatch for custom skeletons — each slot gets a dropdown of the actor's
 * bone names, pre-selected to the auto-match, and only differences from auto
 * are stored as overrides.
 */

export interface BoneSlot {
  /** Canonical key ('hips', 'leftarm', 'lefthandindex1', …). */
  key: string;
  label: string;
  group: 'body' | 'fingers';
}

const BODY: [string, string][] = [
  ['hips', 'Hips'],
  ['spine', 'Spine'],
  ['spine1', 'Spine 1'],
  ['spine2', 'Spine 2'],
  ['chest', 'Chest'],
  ['neck', 'Neck'],
  ['head', 'Head'],
  ['leftshoulder', 'Left Shoulder'],
  ['leftarm', 'Left Upper Arm'],
  ['leftforearm', 'Left Forearm'],
  ['lefthand', 'Left Hand'],
  ['rightshoulder', 'Right Shoulder'],
  ['rightarm', 'Right Upper Arm'],
  ['rightforearm', 'Right Forearm'],
  ['righthand', 'Right Hand'],
  ['leftupleg', 'Left Upper Leg'],
  ['leftleg', 'Left Lower Leg'],
  ['leftfoot', 'Left Foot'],
  ['lefttoe', 'Left Toes'],
  ['rightupleg', 'Right Upper Leg'],
  ['rightleg', 'Right Lower Leg'],
  ['rightfoot', 'Right Foot'],
  ['righttoe', 'Right Toes'],
];

const FINGER_NAMES: [string, string][] = [
  ['thumb', 'Thumb'],
  ['index', 'Index'],
  ['middle', 'Middle'],
  ['ring', 'Ring'],
  ['pinky', 'Pinky'],
];

export const BONE_SLOTS: BoneSlot[] = [
  ...BODY.map(([key, label]): BoneSlot => ({ key, label, group: 'body' })),
];
for (const side of ['left', 'right'] as const) {
  const sideLabel = side === 'left' ? 'L' : 'R';
  for (const [finger, fingerLabel] of FINGER_NAMES) {
    for (let joint = 1; joint <= 3; joint++) {
      BONE_SLOTS.push({
        key: `${side}hand${finger}${joint}`,
        label: `${sideLabel} ${fingerLabel} ${joint}`,
        group: 'fingers',
      });
    }
  }
}

export interface BoneMapRequest {
  actorId: string;
  actorLabel: string;
  /** All bone names on this actor's source skeleton. */
  boneNames: string[];
  /** Slot key → currently mapped source bone name (auto or override), '' = none. */
  current: Record<string, string>;
  /** Slot key → the auto-match result (for the "(auto)" tag + diff detection). */
  auto: Record<string, string>;
}

export interface BoneMapModalHandlers {
  /** Apply overrides (slot → bone name, '' = unassigned); only diffs from auto. */
  onApply: (actorId: string, overrides: Map<string, string>) => void;
}

export function createBoneMapModal(handlers: BoneMapModalHandlers) {
  const modal = byId<HTMLDivElement>('bonemap-modal');
  const title = byId<HTMLElement>('bonemap-title');
  const bodyList = byId<HTMLDivElement>('bonemap-body');
  const fingerList = byId<HTMLDivElement>('bonemap-fingers');
  const applyBtn = byId<HTMLButtonElement>('bonemap-apply');
  const cancelBtn = byId<HTMLButtonElement>('bonemap-cancel');

  let request: BoneMapRequest | null = null;
  let selects = new Map<string, HTMLSelectElement>();

  cancelBtn.addEventListener('click', () => (modal.hidden = true));
  modal.addEventListener('click', (e) => {
    if (e.target === modal) modal.hidden = true;
  });

  applyBtn.addEventListener('click', () => {
    if (!request) return;
    const overrides = new Map<string, string>();
    for (const [key, select] of selects) {
      const chosen = select.value;
      const auto = request.auto[key] ?? '';
      if (chosen !== auto) overrides.set(key, chosen);
    }
    modal.hidden = true;
    handlers.onApply(request.actorId, overrides);
  });

  function buildRow(slot: BoneSlot, req: BoneMapRequest): HTMLElement {
    const row = document.createElement('label');
    row.className = 'bonemap-row';
    const caption = document.createElement('span');
    caption.textContent = slot.label;
    const select = document.createElement('select');

    const none = document.createElement('option');
    none.value = '';
    none.textContent = '— none —';
    select.appendChild(none);
    for (const name of req.boneNames) {
      const opt = document.createElement('option');
      opt.value = name;
      opt.textContent = name === req.auto[slot.key] ? `${name} (auto)` : name;
      select.appendChild(opt);
    }
    select.value = req.current[slot.key] ?? '';
    select.addEventListener('change', () => {
      row.classList.toggle('remapped', select.value !== (req.auto[slot.key] ?? ''));
    });
    row.classList.toggle('remapped', select.value !== (req.auto[slot.key] ?? ''));

    row.append(caption, select);
    selects.set(slot.key, select);
    return row;
  }

  function open(req: BoneMapRequest): void {
    request = req;
    selects = new Map();
    title.textContent = `Bone mapping — ${req.actorLabel}`;
    bodyList.innerHTML = '';
    fingerList.innerHTML = '';
    for (const slot of BONE_SLOTS) {
      (slot.group === 'body' ? bodyList : fingerList).appendChild(buildRow(slot, req));
    }
    modal.hidden = false;
  }

  return { open };
}

function byId<T extends HTMLElement>(id: string): T {
  const el = document.getElementById(id);
  if (!el) throw new Error(`Missing element #${id}`);
  return el as T;
}
